import { Alert, Button, Card, CardBody, CardFooter, CardTitle, Content } from '@patternfly/react-core';
import React, { useState } from 'react';
import { useApiFetch } from '../../api';
import type { ApiResponseClientJoin } from '../../types/api/ApiResponseClient';
import type { ClientJoinRequest, OrganizationRef } from '../../types/models';

export interface JoinOfferData {
  reason: 'domain' | 'vat';
  organization: OrganizationRef;
  canJoin: boolean;
  submittedVat: string; // the VAT the user typed on the create form; resent on a VAT join
}

export interface JoinOfferProps {
  offer: JoinOfferData;
  refreshUser: () => Promise<void>;
  onDecline: () => void;
}

/**
 * The join offer shown when POST /clients was blocked because the caller's email domain or VAT
 * matched an existing organization. Offer-only: it joins (POST /clients/join — the org is
 * re-derived server-side, never sent) or calls onDecline; the decline routing and the
 * domain-blocked terminal live in ClientlessOnboarding. Feedback is an inline Alert — the
 * notification drawer is not mounted on a gate screen.
 */
export function JoinOffer({ offer, refreshUser, onDecline }: JoinOfferProps) {
  const apiFetch = useApiFetch();
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orgName = offer.organization.name;

  const handleJoin = async () => {
    setIsJoining(true);
    setError(null);
    // reason → via (D4): domain → { via: 'domain' }; vat → { via: 'vat', vat_number }.
    const body: ClientJoinRequest =
      offer.reason === 'vat' ? { via: 'vat', vat_number: offer.submittedVat } : { via: 'domain' };
    try {
      const response = await apiFetch('/clients/join', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = (await response.json()) as ApiResponseClientJoin;
      // Success is status:'success' + data — never response.ok / 422 / 'fail'.
      if (data.status === 'success' && data.data) {
        await refreshUser(); // → join_pending; this component unmounts
        return;
      }
      setError(data.message ?? 'We could not complete the join request. Please try again.');
    } catch {
      setError('We could not reach the server. Please check your connection and try again.');
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <Card style={{ maxWidth: '600px' }}>
      <CardTitle>Join an existing organization</CardTitle>
      <CardBody>
        {error && (
          <Alert variant="danger" isInline title="Could not join organization" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        {offer.reason === 'domain' ? (
          <Content component="p">
            Your email address belongs to a domain verified by <strong>{orgName}</strong>. Would you like to join this
            organization?
          </Content>
        ) : (
          <Content component="p">
            An organization with that VAT number already exists — <strong>{orgName}</strong>. Would you like to join it?
          </Content>
        )}
        {!offer.canJoin && (
          <Content component="p">
            Automatic joining isn&apos;t available for this organization. Please contact an administrator to be added.
          </Content>
        )}
      </CardBody>
      <CardFooter>
        <Button
          variant="primary"
          onClick={() => void handleJoin()}
          isLoading={isJoining}
          isDisabled={!offer.canJoin || isJoining}
          spinnerAriaValueText="Joining organization"
        >
          Join {orgName}
        </Button>
        <Button variant="link" onClick={onDecline} isDisabled={isJoining}>
          No, this isn&apos;t my organization
        </Button>
      </CardFooter>
    </Card>
  );
}
